import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, errText, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, Loading, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Users, Star, Plus, Search, Crown, Check, X, Loader2, Shield, TrendingUp, Camera, Mail } from "lucide-react";
import { crewAccessSummary, crewAccessLabel } from "../lib/crewAccess";
import { toast } from "sonner";

export default function CrewsDiscover() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [crews, setCrews] = useState(null);
  const [meta, setMeta] = useState({ categories: [], worlds: [] });
  const [cat, setCat] = useState("All");
  const [world, setWorld] = useState("Global");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("trending");
  const [createOpen, setCreateOpen] = useState(false);
  const [invites, setInvites] = useState([]);

  const load = () => {
    setCrews(null);
    const params = new URLSearchParams({ sort });
    if (cat !== "All") params.set("category", cat);
    if (world !== "Global") params.set("world", world);
    if (q) params.set("q", q);
    api.get(`/crews?${params}`).then(({ data }) => setCrews(data)).catch(() => setCrews([]));
  };
  useEffect(() => { api.get("/crews/meta").then(({ data }) => setMeta(data)); }, []);
  useEffect(() => { load(); }, [cat, world, sort]); // eslint-disable-line
  useEffect(() => { if (user) api.get("/crews/invitations").then(({ data }) => setInvites(data)).catch(() => {}); }, [user]);

  const isPlus = user?.plan === "plus" || user?.role === "admin";
  const actInvite = async (crewId, action) => { try { await api.post(`/crews/invitations/${crewId}/action?action=${action}`); setInvites((x) => x.filter((i) => i.crew.id !== crewId)); if (action === "accept") toast.success("Joined crew!"); } catch (e) { toast.error(errText(e)); } };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
      <div className="relative overflow-hidden rounded-3xl surface glass-gold p-6 md:p-8 mb-6">
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-gold text-xs font-semibold tracking-widest uppercase mb-2"><Shield size={14} /> VCrews</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl">Build Your Circle. <span className="text-gold-grad">Build Your Legacy.</span></h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-xl">Join official entertainment crews — music groups, labels, fashion houses, gaming orgs and virtual families — and climb the VCharts together.</p>
          <div className="flex items-center gap-2 mt-4">
            {isPlus
              ? <Button onClick={() => setCreateOpen(true)} className="btn-gold rounded-full" data-testid="create-crew-btn"><Plus size={16} className="mr-1" />Create a Crew</Button>
              : <Button onClick={() => nav("/plus")} className="btn-gold rounded-full" data-testid="create-crew-plus-cta"><Crown size={16} className="mr-1" />Go VirtuStars+ to create</Button>}
            <Button onClick={() => nav("/charts")} variant="outline" className="rounded-full border-white/15 bg-white/5"><TrendingUp size={15} className="mr-1" />Crew Charts</Button>
          </div>
        </div>
      </div>

      {invites.length > 0 && (
        <div className="surface rounded-2xl p-4 mb-6" data-testid="crew-invites">
          <div className="text-sm font-semibold mb-2 flex items-center gap-2"><Mail size={15} className="text-eblue" /> Crew Invitations</div>
          <div className="space-y-2">{invites.map(({ crew }) => (
            <div key={crew.id} className="flex items-center gap-3 surface-2 rounded-xl p-2.5">
              <CrewEmblem crew={crew} size={36} />
              <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{crew.name}</div><div className="text-xs text-muted-foreground">@{crew.handle}</div></div>
              <Button onClick={() => actInvite(crew.id, "accept")} size="sm" className="btn-blue rounded-lg" data-testid={`invite-accept-${crew.handle}`}>Accept</Button>
              <Button onClick={() => actInvite(crew.id, "decline")} size="sm" variant="outline" className="rounded-lg border-white/15 bg-white/5">Decline</Button>
            </div>
          ))}</div>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center gap-2 surface-2 rounded-full px-4 h-11 mb-4 max-w-md">
        <Search size={16} className="text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search crews by name or @handle" className="bg-transparent flex-1 text-sm outline-none" data-testid="crew-search" />
      </form>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button onClick={() => setCat("All")} className={chip(cat === "All")} data-testid="crew-cat-All">All</button>
        {meta.categories.map((c) => <button key={c} onClick={() => setCat(c)} className={chip(cat === c)} data-testid={`crew-cat-${c}`}>{c}</button>)}
        <div className="ml-auto flex items-center gap-1 surface-2 rounded-full p-1">
          <button onClick={() => setSort("trending")} className={`text-xs px-3 py-1.5 rounded-full ${sort === "trending" ? "bg-gold text-black font-semibold" : "text-muted-foreground"}`} data-testid="crew-sort-trending">Trending</button>
          <button onClick={() => setSort("new")} className={`text-xs px-3 py-1.5 rounded-full ${sort === "new" ? "bg-gold text-black font-semibold" : "text-muted-foreground"}`} data-testid="crew-sort-new">Newest</button>
        </div>
      </div>

      {!crews ? <Loading /> : crews.length === 0 ? <Empty icon={Users} title="No crews yet" hint={isPlus ? "Be the first to found a crew." : "Go VirtuStars+ to found the first crew."} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="crews-grid">
          {crews.map((c) => <CrewCard key={c.id} c={c} />)}
        </div>
      )}

      <CreateCrewModal open={createOpen} onClose={() => setCreateOpen(false)} meta={meta} onCreated={(c) => nav(`/crew/@${c.handle}`)} />
    </div>
  );
}

const chip = (active) => `text-xs rounded-full px-3 py-1.5 border transition ${active ? "bg-gold text-black border-gold font-semibold" : "surface-2 border-white/10 text-muted-foreground hover:text-white"}`;

export function CrewEmblem({ crew, size = 44 }) {
  if (crew.emblem_url) return <img src={mediaUrl(crew.emblem_url)} style={{ width: size, height: size }} className="rounded-full object-cover shrink-0" alt="" />;
  return <div style={{ width: size, height: size }} className="rounded-full shrink-0 bg-gradient-to-br from-gold/30 to-eblue/20 flex items-center justify-center font-display font-bold text-gold" >{(crew.abbreviation || crew.name || "?").slice(0, 2).toUpperCase()}</div>;
}

function CrewCard({ c }) {
  return (
    <Link to={`/crew/@${c.handle}`} className="surface rounded-2xl overflow-hidden hover:ring-1 hover:ring-gold/40 transition group" data-testid={`crew-card-${c.handle}`}>
      <div className="h-20 bg-gradient-to-br from-[#131826] to-black relative">{c.banner_url && <img src={mediaUrl(c.banner_url)} className="w-full h-full object-cover" alt="" />}</div>
      <div className="px-4 pb-4 -mt-8">
        <CrewEmblem crew={c} size={56} />
        <div className="mt-2 font-display font-bold text-lg leading-tight group-hover:text-gold transition">{c.name}</div>
        <div className="text-xs text-muted-foreground">@{c.handle}</div>
        {c.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{c.description}</p>}
        <div className="flex flex-wrap gap-1 mt-2">{(c.categories || []).slice(0, 3).map((cat) => <span key={cat} className="text-[10px] surface-2 rounded-full px-2 py-0.5">{cat}</span>)}</div>
        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Users size={12} />{c.member_count}/{c.member_limit}</span>
          <span className="flex items-center gap-1 text-gold"><Star size={12} />{c.score}</span>
          <span className="ml-auto text-eblue">{crewAccessLabel(c.content_visibility, c.join_policy ?? ({ request: "request", invite: "invite" }[c.privacy] || "open"))}</span>
        </div>
      </div>
    </Link>
  );
}

function CreateCrewModal({ open, onClose, meta, onCreated }) {
  const emblemRef = useRef(); const bannerRef = useRef();
  const [f, setF] = useState({ name: "", handle: "", abbreviation: "", description: "", primary_world: "Second Life", categories: [], content_visibility: "public", join_policy: "open", member_limit: "", emblem_url: null, banner_url: null });
  const [hCheck, setHCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    const h = (f.handle || "").replace(/^@/, "");
    if (!h) { setHCheck(null); return; }
    setHCheck({ loading: true });
    const t = setTimeout(() => api.get(`/crews/handle-check?handle=${encodeURIComponent(h)}`).then(({ data }) => setHCheck(data)).catch(() => setHCheck(null)), 400);
    return () => clearTimeout(t);
  }, [f.handle]);

  const upload = async (file, key) => {
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set(key, data.url); }
    catch (e) { toast.error("Upload failed"); }
  };
  const toggleCat = (c) => set("categories", f.categories.includes(c) ? f.categories.filter((x) => x !== c) : f.categories.length < 4 ? [...f.categories, c] : f.categories);

  const submit = async () => {
    if (!f.name.trim() || !f.handle.trim()) return toast.error("Name and @handle required");
    if (hCheck && !hCheck.available) return toast.error(hCheck.reason);
    setBusy(true);
    try {
      const payload = { ...f, member_limit: f.member_limit ? Number(f.member_limit) : null };
      const { data } = await api.post("/crews", payload);
      toast.success("Crew created!");
      onClose(); onCreated(data);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="surface border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Found a Crew</DialogTitle><DialogDescription>VirtuStars+ members can create official crews.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="relative h-24 rounded-xl bg-gradient-to-br from-[#131826] to-black overflow-hidden cursor-pointer" onClick={() => bannerRef.current?.click()} data-testid="crew-banner-upload">
            {f.banner_url ? <img src={mediaUrl(f.banner_url)} className="w-full h-full object-cover" alt="" /> : <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground gap-1"><Camera size={14} />Add banner</div>}
            <input ref={bannerRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], "banner_url")} />
          </div>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full surface-2 border border-white/10 overflow-hidden cursor-pointer flex items-center justify-center shrink-0" onClick={() => emblemRef.current?.click()} data-testid="crew-emblem-upload">
              {f.emblem_url ? <img src={mediaUrl(f.emblem_url)} className="w-full h-full object-cover" alt="" /> : <Camera size={16} className="text-muted-foreground" />}
              <input ref={emblemRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0], "emblem_url")} />
            </div>
            <div className="flex-1 space-y-2">
              <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Crew name" className="surface-2 border-white/10" data-testid="crew-name-input" />
              <Input value={f.abbreviation} onChange={(e) => set("abbreviation", e.target.value)} placeholder="Abbrev (e.g. SS)" maxLength={8} className="surface-2 border-white/10" data-testid="crew-abbrev-input" />
            </div>
          </div>
          <div>
            <div className="flex items-center surface-2 border border-white/10 rounded-md h-10 px-3"><span className="text-muted-foreground">@</span><input value={f.handle} onChange={(e) => set("handle", e.target.value.replace(/^@/, "").replace(/[^A-Za-z0-9_.]/g, ""))} maxLength={30} placeholder="crewhandle" className="bg-transparent flex-1 outline-none text-sm" data-testid="crew-handle-input" />{hCheck?.loading && <Loader2 size={14} className="animate-spin text-muted-foreground" />}{hCheck && !hCheck.loading && hCheck.available && <Check size={15} className="text-emerald-400" data-testid="crew-handle-ok" />}{hCheck && !hCheck.loading && !hCheck.available && <X size={15} className="text-rose-400" data-testid="crew-handle-bad" />}</div>
            {hCheck && !hCheck.loading && <div className={`text-[11px] mt-1 ${hCheck.available ? "text-emerald-400" : "text-rose-400"}`}>{hCheck.reason} · /crew/@{f.handle}</div>}
          </div>
          <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="What is your crew about?" className="surface-2 border-white/10 min-h-[70px]" data-testid="crew-desc-input" />
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Primary world</div>
            <div className="flex flex-wrap gap-1.5">{meta.worlds?.map((w) => <button key={w} onClick={() => set("primary_world", w)} className={chip(f.primary_world === w)}>{w}</button>)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Categories (up to 4)</div>
            <div className="flex flex-wrap gap-1.5">{meta.categories?.map((c) => <button key={c} onClick={() => toggleCat(c)} className={chip(f.categories.includes(c))} data-testid={`crew-cat-opt-${c}`}>{c}</button>)}</div>
          </div>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-muted-foreground mb-1.5">Content visibility</div>
              <select value={f.content_visibility} onChange={(e) => set("content_visibility", e.target.value)} className="w-full surface-2 border border-white/10 rounded-md h-10 px-2 text-sm" data-testid="crew-visibility-select">
                <option value="public">Public — anyone can view posts & media</option>
                <option value="members">Members only — content hidden until you join</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1.5">Who can join</div>
                <select value={f.join_policy} onChange={(e) => set("join_policy", e.target.value)} className="w-full surface-2 border border-white/10 rounded-md h-10 px-2 text-sm" data-testid="crew-join-select">
                  <option value="open">Open — anyone joins</option>
                  <option value="request">Request — approve each</option>
                  <option value="invite">Invite only</option>
                </select>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1.5">Member limit</div>
                <Input type="number" value={f.member_limit} onChange={(e) => set("member_limit", e.target.value)} placeholder="Default 10" className="surface-2 border-white/10" data-testid="crew-limit-input" />
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground surface-2 rounded-lg p-2.5" data-testid="crew-access-summary">{crewAccessSummary(f.content_visibility, f.join_policy)}</div>
          </div>
          <Button onClick={submit} disabled={busy} className="btn-gold rounded-xl w-full" data-testid="crew-create-submit">{busy ? "Creating…" : "Create Crew"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
